// eslint-disable-next-line @github-ui/github-monorepo/no-sx
import {Box, Text, type TextProps, type SxProp} from '@primer/styled-react'
import type {PolymorphicForwardRefComponent} from '@github-ui/react-polymorphic'
import type React from 'react'
import {forwardRef} from 'react'
import type {SafeHTMLString} from './safe-html'

interface VerifiedHTMLProps {
  /**
   * Set the rendered HTML of the component. To prevent XSS, ensure that the source of this
   * HTML is trusted! If the source is untrusted, use the `UnsafeHTML` component instead.
   */
  html?: SafeHTMLString
}

type PropsWithHTML<T> = T & VerifiedHTMLProps

/**
 * @deprecated Use `VerifiedHTMLDiv` or `VerifiedHTMLText` instead.
 *
 * `VerifiedHTMLBox` extends `Box` from `@primer/react` to allow injecting HTML strings
 * into the DOM.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLBox` component instead.
 */
// eslint-disable-next-line @github-ui/github-monorepo/no-sx
export const VerifiedHTMLBox = withSafeHTML<SxProp>(Box) as PolymorphicForwardRefComponent<
  'div' | 'span' | 'pre' | 'table' | 'tbody' | 'tr' | 'td' | 'ul' | 'ol' | 'li',
  // eslint-disable-next-line @github-ui/github-monorepo/no-sx
  PropsWithHTML<SxProp>
>

/**
 * `VerifiedHTMLText` extends `Text` from `@primer/react` to allow injecting HTML strings
 * into the DOM.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLText` component instead.
 */
export const VerifiedHTMLText = withSafeHTML<TextProps>(Text) as PolymorphicForwardRefComponent<
  'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a' | 'td',
  PropsWithHTML<
    TextProps<'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a' | 'td'>
  >
>

type DivProps = VerifiedHTMLProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

const Div = forwardRef<HTMLDivElement, DivProps>((props, ref) => <div {...props} ref={ref} />)
Div.displayName = 'Div'

/**
 * `VerifiedHTMLDiv` extends `div` with props for safely rendering HTML strings.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLDiv` component instead.
 */
export const VerifiedHTMLDiv = withSafeHTML<DivProps>(Div)

type BdiProps = VerifiedHTMLProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>

const Bdi = forwardRef<HTMLElement, BdiProps>((props, ref) => <bdi {...props} ref={ref} />)
Bdi.displayName = 'Bdi'

/**
 * `VerifiedHTMLBdi` extends `bdi` with props for safely rendering HTML strings.
 *
 * This component should only be used with strings that have specifically been marked as verified,
 * via the `SafeHTMLString` type.
 *
 * Only mark strings as `SafeHTMLString` if one of the following applies:
 * - they come from a trusted source on the server (eg the HTML pipeline)
 * - they have known static contents
 * - they have been sanitized by DOMPurify.
 *
 * All other strings should use the `UnsafeHTMLBdi` component instead.
 */
export const VerifiedHTMLBdi = withSafeHTML<BdiProps>(Bdi)

/**
 * A higher-order component that extends a basic component to inject an
 * `html` prop into the DOM via `dangerouslySetInnerHTML`.
 */
function withSafeHTML<T>(Component: React.ComponentType<T>) {
  const SafeHTMLComponent = forwardRef<Element, PropsWithHTML<T>>((propsWithHtml, ref) => {
    const {html, ...props} = propsWithHtml
    // This is the only place in the codebase where `dangerouslySetInnerHTML` should be allowed
    // eslint-disable-next-line react/forbid-component-props
    return <Component ref={ref} {...(props as T)} dangerouslySetInnerHTML={html ? {__html: html} : undefined} />
  })
  SafeHTMLComponent.displayName = `SafeHTML${Component.displayName || Component.name}`

  return SafeHTMLComponent
}

try{ VerifiedHTMLBox.displayName ||= 'VerifiedHTMLBox' } catch {}
try{ VerifiedHTMLText.displayName ||= 'VerifiedHTMLText' } catch {}
try{ VerifiedHTMLDiv.displayName ||= 'VerifiedHTMLDiv' } catch {}
try{ VerifiedHTMLBdi.displayName ||= 'VerifiedHTMLBdi' } catch {}